import { useEffect, useMemo, useReducer, useState } from "react";

import { DivisionReport, UsageVarianceOutputType } from "@asd/customer-portal-api";
import { ReportKey, useOrganization } from "@asd/customer-portal-ui";
import { Box, Divider, Paper, Stack, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation } from "react-router-dom";

import { FilterAutocomplete, FilterTextField } from "~/components/shared";
import { ReportsService } from "~/services";
import { fuzzyMatch } from "~/utils/fuzzyMatch";
import {
  buildMonthOptionFromValue,
  DIVISION_COLUMNS,
  getColumnGroupingModel,
  getColumns,
  getColumnValue,
  getStandardMonthOptions,
} from "./helpers";
import MonthsAutocomplete, { MonthOption } from "./MonthsAutocomplete";
import ReportsExportToolbar from "./ReportsExport";
import { ReportsData, useReportsCache } from "./useReportsCache";

const Reports = () => {
  const theme = useTheme();
  const location = useLocation();
  const {
    configuration: { reports, supportCoordinator },
  } = useOrganization();

  const { getReportsData, setReportsData } = useReportsCache();

  const stateReportKey = (location.state?.reportKey as ReportKey) ?? null;
  const stateMonthValue = (location.state?.monthValue as string) ?? null;

  const standardMonthOptions = useMemo<MonthOption[]>(() => getStandardMonthOptions(), []);

  // null when no report months are available yet (e.g. last month is before the authorization start date cutoff)
  const initialMonth = useMemo<MonthOption | null>(() => {
    return stateMonthValue ? buildMonthOptionFromValue(stateMonthValue) : (standardMonthOptions[0] ?? null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const standardMonthValues = useMemo<Set<string>>(() => new Set(standardMonthOptions.map((o) => o.value)), [standardMonthOptions]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportKey>(stateReportKey ?? "summary");
  const [selectedMonth, setSelectedMonth] = useState<MonthOption | null>(initialMonth);
  const [extraMonthOptions] = useState<MonthOption[]>(() => {
    if (stateMonthValue && !standardMonthValues.has(stateMonthValue)) {
      return [buildMonthOptionFromValue(stateMonthValue)];
    }
    return [];
  });
  const [data, setData] = useState<ReportsData>({
    summary: [] as any[],
    overUsage: [] as any[],
    unused: [] as any[],
    depleted: [] as any[],
    overtime: [] as any[],
    division: [] as any[],
  });

  const selectedIndex = reports.tabs.findIndex((t) => t.value === selectedReport);

  const activeIndicatorColor = reports.tabs[selectedIndex].indicatorColor
    .split(".")
    .reduce((obj: any, key) => obj?.[key], theme.palette) as string;

  const [tableFilters, setTableFilters] = useReducer(
    (state, action) => ({
      ...state,
      [action.key]: action.value,
    }),
    {
      program: [],
      name: "",
    },
  );

  const columns = useMemo(() => getColumns(selectedReport, supportCoordinator), [selectedReport, supportCoordinator]);
  const columnGroupingModel = useMemo(() => getColumnGroupingModel(selectedReport), [selectedReport]);

  const programOptions = useMemo(() => {
    if (isLoading || !data[selectedReport]?.length) {
      return [];
    }

    const programs = new Set<string>();
    const columnField = columns.some((col) => col.field === "program") ? "program" : "programName";
    data[selectedReport].forEach((row: any) => {
      const programVal = String(getColumnValue(columns, columnField, row) || "");
      if (programVal) {
        programs.add(programVal);
      }
    });
    return Array.from(programs).sort();
  }, [isLoading, data, columns, selectedReport]);

  const filteredData = useMemo(() => {
    const filterKeys = Object.keys(tableFilters);

    return data[selectedReport].filter((row: any) => {
      return filterKeys.every((key) => {
        const filterValue = tableFilters[key as keyof typeof tableFilters];
        if (!filterValue) {
          return true;
        }

        if (key === "program") {
          const columnField = columns.some((col) => col.field === "program") ? "program" : "programName";
          const programVal = String(getColumnValue(columns, columnField, row) || "");
          return (filterValue as Array<string>).includes(programVal);
        }

        const stringFilter = typeof filterValue === "string" ? filterValue : "";
        if (key === "name") {
          const programAdminVal = String(getColumnValue(columns, "programAdmin", row) || "");
          const careManagerVal = String(getColumnValue(columns, "careManager", row) || "");
          const memberVal = String(getColumnValue(columns, "member", row) || "");

          return (
            fuzzyMatch(programAdminVal, stringFilter) || fuzzyMatch(careManagerVal, stringFilter) || fuzzyMatch(memberVal, stringFilter)
          );
        }

        const rawValue = getColumnValue(columns, key, row);
        if (typeof rawValue === "string") {
          return fuzzyMatch(rawValue, stringFilter);
        }
        return rawValue === filterValue;
      });
    });
  }, [data, columns, selectedReport, tableFilters]);

  const noRowsMessage = useMemo(() => {
    if (hasError) {
      return "Failed to load report data";
    }
    if (!data[selectedReport]?.length) {
      return "No report this month";
    }
    return "Try broadening your filters or search criteria to see more results";
  }, [hasError, data, selectedReport]);

  useEffect(() => {
    if (!selectedMonth) {
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const summaryController = new AbortController();
    const overUsageController = new AbortController();
    const unusedController = new AbortController();
    const depletedController = new AbortController();
    const overtimeController = new AbortController();
    const divisionController = new AbortController();
    (async () => {
      setIsLoading(true);
      setHasError(false);

      const cachedData = getReportsData(selectedMonth.value);
      if (cachedData) {
        if (isActive) {
          setData(cachedData as ReportsData);
          setIsLoading(false);
        }
        return;
      }

      try {
        const [summaryResponse, overUsageResponse, unusedResponse, depletedResponse, overtimeResponse, divisionResponse] =
          await Promise.all([
            ReportsService.getSummary(
              `${selectedMonth.value}-${selectedMonth.lastDay}`,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              { signal: summaryController.signal },
            ),
            ReportsService.getUsageVariance(
              `${selectedMonth.value}-${selectedMonth.lastDay}`,
              undefined,
              undefined,
              undefined,
              UsageVarianceOutputType.OVER,
              undefined,
              { signal: overUsageController.signal },
            ),
            ReportsService.getUsageVariance(
              `${selectedMonth.value}-${selectedMonth.lastDay}`,
              undefined,
              100,
              "P1M",
              UsageVarianceOutputType.UNDER,
              undefined,
              { signal: unusedController.signal },
            ),
            ReportsService.getUsageDepleted(`${selectedMonth.value}-${selectedMonth.lastDay}`, undefined, undefined, undefined, {
              signal: depletedController.signal,
            }),
            ReportsService.getOvertimeUsage(`${selectedMonth.value}-01`, `${selectedMonth.value}-${selectedMonth.lastDay}`, undefined, {
              signal: overtimeController.signal,
            }),
            ReportsService.getDivisionReport(`${selectedMonth.value}-${selectedMonth.lastDay}`, { signal: divisionController.signal }),
          ]);

        if (isActive) {
          const [summary, overUsage, unused, depleted, overtime, division] = [
            summaryResponse?.data?.highlightsSummaries ?? [],
            overUsageResponse?.data?.authorizationUsageVariances ?? [],
            unusedResponse?.data?.authorizationUsageVariances ?? [],
            depletedResponse?.data?.authorizationUsageDepletions ?? [],
            overtimeResponse?.data?.authorizationUsageOvertimes ?? [],
            (divisionResponse?.data ?? []).sort((a: DivisionReport, b: DivisionReport) => {
              const aName = String(getColumnValue(DIVISION_COLUMNS, "lastName", a) || "").toLowerCase();
              const bName = String(getColumnValue(DIVISION_COLUMNS, "lastName", b) || "").toLowerCase();
              return aName.localeCompare(bName);
            }),
          ].map((data) =>
            data.map((item: any) => {
              const { authorization, ...usage } = item;
              return { ...authorization, ...usage };
            }),
          );

          const newData = { summary, overUsage, unused, depleted, overtime, division };
          setData(newData);
          setReportsData(selectedMonth.value, newData);
        }
      } catch {
        if (isActive) {
          setHasError(true);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
      summaryController.abort();
      overUsageController.abort();
      unusedController.abort();
      depletedController.abort();
      overtimeController.abort();
      divisionController.abort();
    };
  }, [getReportsData, selectedMonth, setReportsData]);

  return (
    <Box sx={{ p: 1, height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Paper elevation={1} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} overflow="auto" pt={0.5} px={1}>
          <Tabs
            onChange={(_event, newValue) => setSelectedReport(newValue)}
            slotProps={{
              indicator: {
                style: {
                  height: "100%",
                  backgroundColor: activeIndicatorColor,
                  transition:
                    "left 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1), background-color 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 0,
                  bottom: 0,
                  borderRadius: 0,
                },
              },
            }}
            sx={{
              minHeight: 40,
              flexShrink: 0,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              "& .MuiTabs-flexContainer": { gap: 0 },
              "& .MuiTab-root": {
                ...theme.typography.body2,
                minHeight: 40,
                minWidth: "unset",
                px: 1.5,
                py: 0,
                zIndex: 1,
                position: "relative",
                opacity: isLoading || !selectedMonth ? 0.5 : 1,
                transition: "color 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                borderRight: "1px solid",
                borderColor: "divider",
                "&:last-of-type": { borderRight: "none" },
                "&.Mui-selected": { color: "common.white" },
              },
            }}
            value={selectedReport}
          >
            {reports.tabs.map(({ value, label, Icon }) => (
              <Tab
                key={value}
                disabled={isLoading || !selectedMonth}
                icon={<Icon fontSize="small" />}
                iconPosition="start"
                label={label}
                value={value}
              />
            ))}
          </Tabs>
          <MonthsAutocomplete extraOptions={extraMonthOptions} onChange={(newMonth) => setSelectedMonth(newMonth)} value={selectedMonth} />
          <Divider orientation="vertical" sx={{ my: 0.5 }} flexItem />
          <ReportsExportToolbar
            columns={columns}
            disabled={isLoading || !filteredData.length}
            month={selectedMonth?.label ?? ""}
            reportLabel={reports.tabs[selectedIndex].label}
            rows={filteredData}
          />
        </Stack>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} mt={1} overflow="auto" pt={0.5} px={1}>
          <FilterAutocomplete
            disabled={isLoading}
            filterKey="program"
            filters={tableFilters}
            label="Filter by Program"
            options={programOptions}
            setFilters={setTableFilters}
            sx={{
              flex: 1,
            }}
          />
          <FilterTextField
            disabled={isLoading}
            filterKey="name"
            filters={tableFilters}
            label="Search by Name"
            setFilters={setTableFilters}
            sx={{
              flexGrow: 1,
              width: 0,
              minWidth: "10rem",
              height: "100%",
            }}
          />
        </Stack>
        <Divider sx={{ m: 1 }} />
        <DataGrid
          key={selectedReport}
          columnGroupingModel={columnGroupingModel}
          columns={columns}
          getRowId={(row) => row.authorizationServiceId ?? row.memberId}
          loading={isLoading}
          rows={filteredData}
          showToolbar={false}
          {...({ tableFilters } as any)}
          slotProps={{
            loadingOverlay: {
              variant: "circular-progress",
              noRowsVariant: "circular-progress",
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Typography sx={{ justifySelf: "center", height: "100%", alignContent: "center" }}>{noRowsMessage}</Typography>
            ),
          }}
          sx={{
            pointerEvents: isLoading ? "none" : "auto",
            opacity: isLoading ? 0.7 : 1,
            "& .centerColumnGroupHeader .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "center",
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};

export default Reports;
